import type { PersonChipBookingStatus } from "components/common/PersonChip";
import type { SearchResultItem } from "components/common/SearchInputWithDropdown";
import type { UserWithRole } from "api/auth/Auth.typed";
import { userDisplayName } from "utils/userHelper";
import {
  isPersonPicklistParamType,
  type ParamValueType,
} from "../../template/display/paramsTemplateUtils";
import type { ExcursionGroupCardClient, ExcursionGroupCardUser } from "./excursionPlanningTypes";

/** Guides (excursion templates use type `guides`); gated by profile `User.is_guide`. */
export function isUserGuideForExcursionParams(u: UserWithRole): boolean {
  return u.user.is_guide === true;
}

/** Convert ISO or YYYY-MM-DD string to date input value (YYYY-MM-DD). */
export function toDateInputValue(isoOrYmd: string): string {
  return isoOrYmd.slice(0, 10);
}

/** Filter params template to Record<string, string> (key -> type). */
export function filterTemplateToRecord(
  template: Record<string, unknown> | undefined,
): Record<string, string> {
  const t = template ?? {};

  const filtered: Record<string, string> = {};

  for (const [k, v] of Object.entries(t)) {
    if (typeof v !== "string") continue;

    const normalized = v.trim().toLowerCase();

    if (["users", "user", "utilisateur", "utilisateurs"].includes(normalized)) {
      filtered[k] = "users";

      continue;
    }

    if (["customers", "customer", "client", "clients"].includes(normalized)) {
      filtered[k] = "customers";

      continue;
    }

    if (["guides", "guide", "encadrant", "encadrants"].includes(normalized)) {
      filtered[k] = "guides";

      continue;
    }

    if (["number", "string", "boolean", "users", "customers", "guides"].includes(normalized)) {
      filtered[k] = normalized;
    }
  }

  return filtered;
}

/**
 * Drops param values that are just the declared scalar type name (e.g. value "string" for a string field).
 * Prevents showing type names inside inputs when the API or legacy payloads echoed the template type as value.
 */
export function sanitizeExcursionParamsAgainstTemplate(
  template: Record<string, string>,
  params: Record<string, unknown>,
): Record<string, unknown> {
  if (template == null || Object.keys(template).length === 0) return { ...params };
  const next: Record<string, unknown> = { ...params };
  for (const [k, type] of Object.entries(template)) {
    const t = type.trim().toLowerCase();

    if (t === "boolean" && (next[k] === undefined || next[k] === null)) {
      next[k] = false;

      continue;
    }

    if (!["string", "number", "boolean"].includes(t)) continue;

    const v = next[k];
    if (v == null || v === "") continue;
    if (String(v).trim().toLowerCase() === t) {
      delete next[k];
    }
  }

  return next;
}

/** Drop UI-only SearchResult fields (e.g. `disabled`, `conflictHint`) from person picklists before API persist. */
export function stripPicklistSearchMetadataForPersist(
  template: Record<string, string>,
  params: Record<string, unknown>,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...params };

  for (const [k, rawType] of Object.entries(template)) {
    if (!isPersonPicklistParamType(rawType)) continue;

    const v = next[k];

    if (Array.isArray(v)) {
      const mapped = v
        .map((item) => normalizePicklistPersistItem(item))
        .filter((item): item is { id: number; label: string } => item != null);
      next[k] = mapped;

      continue;
    }

    if (v && typeof v === "object" && "id" in v) {
      const one = normalizePicklistPersistItem(v);

      next[k] = one != null ? [one] : [];
    }
  }

  return next;
}

function normalizePicklistPersistItem(v: unknown): { id: number; label: string } | null {
  if (v == null || typeof v !== "object") return null;

  const o = v as { id?: unknown; label?: unknown };

  if (typeof o.id !== "number") return null;

  return {
    id: o.id,
    label: typeof o.label === "string" ? o.label : String(o.label ?? ""),
  };
}

/** Maps API `booking_status` on param picklists to PersonChip display (response-only). */
export function normalizeBookingStatusForPersonChip(raw: unknown): PersonChipBookingStatus | null {
  if (raw == null || typeof raw !== "string") return null;
  const s = raw.trim().toLowerCase();
  if (s === "pending") return "pending";
  if (s === "validated") return "validated";
  if (s === "rejected") return "rejected";
  if (s === "cancelled") return "cancelled";

  return null;
}

/** Staff excursion param picklists only (`guides` / `users`); customers never carry booking badges. */
export function bookingStatusChipForStaffParamPicker(
  pickerType: ParamValueType,
  item: SearchResultItem,
): PersonChipBookingStatus | null {
  if (pickerType !== "guides" && pickerType !== "users") return null;

  return normalizeBookingStatusForPersonChip(item.booking_status);
}

/** Display name for template (id + name). */
export function templateDisplayName(t: { id?: number; name?: string }): string {
  return t.name || `Modèle #${t.id}`;
}

export type PersonLabels = {
  customer_name?: string;
  customer_first_name?: string;
  customer_last_name?: string;
  customer_qualification?: string;
};

export type PersonDisplayFormat = "lastFirst" | "firstLast";

export function personName(first?: string, last?: string, fallbackId?: number): string {
  const name = [first, last].filter(Boolean).join(" ").trim();
  return name || (fallbackId != null ? `#${fallbackId}` : "");
}

/** Format "Prénom Nom - Qualification" for search display. */
export function firstLastQualificationLabel(first?: string, last?: string, qual?: string): string {
  const name = [first, last].filter(Boolean).join(" ").trim();
  const q = qual?.trim();
  if (!name && !q) return "";
  return q ? `${name} - ${q}` : name;
}

/** Format person/customer label for display. */
export function personDisplayLabel(
  labels?: PersonLabels,
  format: PersonDisplayFormat = "lastFirst",
): string {
  if (!labels) return "—";
  const last = labels.customer_last_name?.trim();
  const first = labels.customer_first_name?.trim();
  const qual = labels.customer_qualification?.trim();
  if (format === "firstLast") {
    const name = [first, last].filter(Boolean).join(" ");
    if (!name && !qual) return labels.customer_name ?? "—";
    return qual ? `${name} - ${qual}` : name;
  }
  const parts = [last, first, qual].filter(Boolean) as string[];
  if (parts.length > 0) return parts.join(" - ");
  return labels.customer_name ?? "—";
}

export type ExcursionCounterCustomerForBilling = {
  customerId: number;
  label: string;
  qualification?: string;
};

/** Unique clients across excursion groups, for compteurs (validation de sortie). */
export function collectUniqueExcursionCustomersForCounters(
  groups: { customers?: ExcursionGroupCardClient[] }[],
): ExcursionCounterCustomerForBilling[] {
  const seen = new Set<number>();
  const out: ExcursionCounterCustomerForBilling[] = [];
  for (const g of groups) {
    for (const c of g.customers ?? []) {
      if (seen.has(c.id)) continue;
      seen.add(c.id);
      const label =
        userDisplayName({ first_name: c.first_name, last_name: c.last_name }).trim() ||
        c.label?.trim() ||
        `Client #${c.id}`;
      const q = c.qualification?.trim();
      out.push({
        customerId: c.id,
        label,
        ...(q ? { qualification: q } : {}),
      });
    }
  }
  return out;
}

export type ExcursionCounterValidationGroupSlice = {
  groupTitle: string;
  guideLine: string;
  customers: ExcursionCounterCustomerForBilling[];
};

/** One slice per groupe with customers deduped in-group; guides line for display. */
export function collectExcursionCounterValidationGroupSlices(
  groups: {
    customers?: ExcursionGroupCardClient[];
    users?: ExcursionGroupCardUser[];
  }[],
): ExcursionCounterValidationGroupSlice[] {
  const slices: ExcursionCounterValidationGroupSlice[] = [];

  groups.forEach((group, idx) => {
    const seenInGroup = new Set<number>();
    const rows: ExcursionCounterCustomerForBilling[] = [];

    for (const c of group.customers ?? []) {
      if (seenInGroup.has(c.id)) continue;

      seenInGroup.add(c.id);
      const label =
        userDisplayName({ first_name: c.first_name, last_name: c.last_name }).trim() ||
        c.label?.trim() ||
        `Client #${c.id}`;
      const q = c.qualification?.trim();

      rows.push({
        customerId: c.id,
        label,
        ...(q ? { qualification: q } : {}),
      });
    }

    if (rows.length === 0) {
      return;
    }

    const guideNames = (group.users ?? [])
      .map((u) => userDisplayName({ first_name: u.first_name, last_name: u.last_name }).trim())
      .filter((s): s is string => s.length > 0);

    const guideLine = guideNames.length > 0 ? guideNames.join(", ") : "—";

    slices.push({
      groupTitle: `Groupe ${idx + 1}`,
      guideLine,
      customers: rows,
    });
  });

  return slices;
}
